import React, { createContext, ReactElement, useEffect, useState } from 'react';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useUploadContext } from 'src/hooks/UploadContextHooks';
import { getMultiNextStep } from 'src/services/SiteFlowService';
import { currentCulture } from 'src/utils/cultureUtil';

export interface StudioContextProps {
  isStudioInitialized: boolean;
  studioUrl?: string;
}

export const StudioContext = createContext<StudioContextProps>({
  isStudioInitialized: false,
});

export interface StudioContextProviderProps {
  children?: React.ReactNode;
}

export const StudioContextProvider = ({ children }: StudioContextProviderProps): ReactElement => {
  const [isStudioInitialized, setStudioInitialized] = useState(false);
  const [studioUrl, setStudioUrl] = useState<string | undefined>();
  const { isPreUploaded } = useUploadContext();
  const {
    productInformation: { productKey, productVersion, selectedOptions, quantity },
  } = useProductContext();

  useEffect(() => {
    let isStale = false;

    async function initialize() {
      try {
        if (!isPreUploaded && productKey) {
          const multiNextStep = await getMultiNextStep(
            currentCulture,
            productKey,
            productVersion,
            selectedOptions,
            quantity
          );
          if (isStale) {
            return;
          }

          setStudioUrl(multiNextStep.flows.find(flow => flow.flowAction === 'studio')?.url);
        }
      } catch (error) {
        console.error(`Error initializing studio context: ${error}`);
      }

      setStudioInitialized(true);
    }

    initialize();

    return () => {
      isStale = true;
    };
  }, [isPreUploaded, productKey, productVersion, selectedOptions, quantity]);

  return <StudioContext.Provider value={{ isStudioInitialized, studioUrl }}>{children}</StudioContext.Provider>;
};
